/**
 * Standardized error handling utilities
 *
 * IMPORTANT: Backend always returns errors in the format: { error: "message" }
 * The ApiError class extracts this into error.message
 */

/**
 * Extract error message from API error
 *
 * @param {Error|ApiError|any} error - Error object from API call
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} Extracted error message
 *
 * @example
 * // In React Query mutation onError:
 * onError: (error) => {
 *   const message = getErrorMessage(error);
 *   alert(`Operation failed:\n\n${message}`);
 * }
 */
export function getErrorMessage(error, fallback = "An error occurred") {
  // ApiError already extracts the message from data.error
  // So we just need to check error.message
  if (error?.message) {
    return error.message;
  }

  // Fallback to data.error if message not set (shouldn't happen with ApiError)
  if (error?.data?.error) {
    return error.data.error;
  }

  // Last resort
  return fallback;
}

/**
 * Check if error is a specific type
 *
 * @param {Error|ApiError|any} error - Error object
 * @param {string[]} keywords - Keywords to check for in error message
 * @returns {boolean} True if error message contains any of the keywords
 *
 * @example
 * if (isErrorType(error, ['token', 'refresh', 'Xero not connected'])) {
 *   // Handle Xero connection error
 * }
 */
export function isErrorType(error, keywords) {
  const message = getErrorMessage(error, "").toLowerCase();
  return keywords.some(keyword => message.includes(keyword.toLowerCase()));
}

/**
 * Format error message for display
 *
 * @param {string} operation - The operation that failed (e.g., "sync invoice")
 * @param {Error|ApiError|any} error - Error object
 * @returns {string} Formatted error message
 *
 * @example
 * const message = formatErrorMessage("sync invoice", error);
 * alert(message); // "Failed to sync invoice:\n\nXero not connected..."
 */
export function formatErrorMessage(operation, error) {
  const message = getErrorMessage(error);
  return `Failed to ${operation}:\n\n${message}`;
}
